import React, { useEffect, useState, useRef, createElement } from 'react';
export function SplineStage() {
  const stageRef = useRef<HTMLDivElement>(null);
  const spotRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLImageElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);
  const [holdProgress, setHoldProgress] = useState(0);
  const [loginStatus, setLoginStatus] = useState<'idle' | 'holding' | 'success' | 'failure'>('idle');
  // Ensure Spline web component is available (safe to add once)
  useEffect(() => {
    const src = 'https://unpkg.com/@splinetool/viewer/build/spline-viewer.js';
    if (!document.querySelector(`script[src="${src}"]`)) {
      const s = document.createElement('script');
      s.type = 'module';
      s.src = src;
      document.head.appendChild(s);
    }
  }, []);
  // Listen for hold progress events
  useEffect(() => {
    const handleHoldProgress = (e: Event) => {
      const customEvent = e as CustomEvent<{
        progress: number;
      }>;
      const progress = customEvent.detail.progress;
      setHoldProgress(progress);
      if (progress > 0 && progress < 100) {
        setLoginStatus('holding');
      } else if (progress === 0) {
        setLoginStatus('idle');
      }
    };
    const handleLoginSuccess = () => {
      setLoginStatus('success');
    };
    const handleLoginFailure = () => {
      setLoginStatus('failure');
      // Reset to idle after animation
      setTimeout(() => setLoginStatus('idle'), 1000);
    };
    window.addEventListener('holdProgress', handleHoldProgress);
    window.addEventListener('loginSuccess', handleLoginSuccess);
    window.addEventListener('loginFailure', handleLoginFailure);
    return () => {
      window.removeEventListener('holdProgress', handleHoldProgress);
      window.removeEventListener('loginSuccess', handleLoginSuccess);
      window.removeEventListener('loginFailure', handleLoginFailure);
    };
  }, []);
  // Spotlight + chest badge motion + reveal
  useEffect(() => {
    const stage = stageRef.current;
    const spot = spotRef.current;
    const badge = badgeRef.current;
    const glow = glowRef.current;
    if (!stage || !spot || !badge || !glow) return;
    // center the flashlight to avoid jump
    const r0 = stage.getBoundingClientRect();
    let tx = r0.width / 2;
    let ty = r0.height / 2;
    let x = tx;
    let y = ty;
    const ease = 0.18;
    const maxParallax = 6;
    const maxTilt = 10;
    const setTarget = (e: MouseEvent | TouchEvent) => {
      const r = stage.getBoundingClientRect();
      const cx = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
      const cy = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
      tx = cx - r.left;
      ty = cy - r.top;
    };
    const leave = () => {
      spot.style.opacity = '0';
    };
    const enter = () => {
      spot.style.opacity = '0.9';
    };
    stage.addEventListener('pointermove', setTarget as any, {
      passive: true
    });
    stage.addEventListener('pointerenter', enter);
    stage.addEventListener('pointerleave', leave);
    stage.addEventListener('touchstart', setTarget as any, {
      passive: true
    });
    stage.addEventListener('touchmove', setTarget as any, {
      passive: true
    });
    stage.addEventListener('touchend', leave);
    let raf = 0;
    const loop = () => {
      x += (tx - x) * ease;
      y += (ty - y) * ease;
      spot.style.transform = `translate(${x - spot.offsetWidth / 2}px, ${y - spot.offsetHeight / 2}px)`;
      const r = stage.getBoundingClientRect();
      const nx = (x - r.width / 2) / (r.width / 2);
      const ny = (y - r.height / 2) / (r.height / 2);
      const dx = nx * maxParallax;
      const dy = ny * maxParallax;
      const ry = nx * maxTilt;
      const rx = -ny * maxTilt;
      const base2D = `translate(calc(-50% + ${dx}px), calc(-50% + -4px + ${dy}px)) rotate(0deg) scale(0.9)`;
      const tilt3D = ` rotateY(${ry}deg) rotateX(${rx}deg)`;
      badge.style.transform = base2D + tilt3D;
      glow.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))${tilt3D}`;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    // Reveal with scene events + 3s fallback
    let revealed = false;
    const reveal = () => {
      if (!revealed) {
        revealed = true;
        setIsReady(true);
      }
    };
    const viewer = stage.querySelector('spline-viewer');
    viewer?.addEventListener('ready', reveal);
    viewer?.addEventListener('load', reveal);
    viewer?.addEventListener('scene-load', reveal);
    const t = setTimeout(reveal, 3000);
    return () => {
      stage.removeEventListener('pointermove', setTarget as any);
      stage.removeEventListener('pointerenter', enter);
      stage.removeEventListener('pointerleave', leave);
      stage.removeEventListener('touchstart', setTarget as any);
      stage.removeEventListener('touchmove', setTarget as any);
      stage.removeEventListener('touchend', leave);
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, []);
  // Calculate glow animation based on hold progress and login status
  const getGlowStyle = () => {
    // Calculate pulse speed based on progress (2.5s at start, 0.3s at 100%)
    const pulseSpeed = 2.5 - holdProgress / 100 * 2.2; // 2.5s -> 0.3s
    if (loginStatus === 'success') {
      return {
        opacity: 1,
        background: 'radial-gradient(circle, rgba(34,197,94,.95) 0%, rgba(34,197,94,.6) 40%, rgba(34,197,94,.3) 60%, rgba(34,197,94,0) 75%)',
        animation: 'none'
      };
    }
    if (loginStatus === 'failure') {
      return {
        opacity: 1,
        background: 'radial-gradient(circle, rgba(239,68,68,.95) 0%, rgba(239,68,68,.6) 40%, rgba(239,68,68,.3) 60%, rgba(239,68,68,0) 75%)',
        animation: 'chest-pulse-red 0.5s ease-in-out 2'
      };
    }
    if (loginStatus === 'holding') {
      return {
        opacity: 1,
        background: 'radial-gradient(circle, rgba(255,255,255,.95) 0%, rgba(255,255,255,.6) 40%, rgba(255,255,255,.3) 60%, rgba(255,255,255,0) 75%)',
        animation: `chest-pulse-white ${pulseSpeed}s ease-in-out infinite`
      };
    }
    // Idle state
    return {
      opacity: isReady ? 0.72 : 0,
      background: 'radial-gradient(circle, rgba(255,255,255,.85) 0%, rgba(255,255,255,.25) 40%, rgba(255,255,255,.08) 60%, rgba(255,255,255,0) 75%)',
      animation: 'none'
    };
  };
  return <div id="cz-stage" ref={stageRef} className={`relative w-full h-full overflow-hidden bg-black ${isReady ? 'is-ready' : ''}`} style={{
    borderRadius: '14px',
    perspective: '900px',
    transformStyle: 'preserve-3d'
  }} data-id="element-50">
      {/* Flashlight behind canvas */}
      <div ref={spotRef} className="absolute left-0 top-0 pointer-events-none" style={{
      zIndex: 0,
      width: '520px',
      height: '520px',
      transform: 'translate(-9999px, -9999px)',
      borderRadius: '50%',
      opacity: 0,
      background: 'radial-gradient(circle, rgba(255,255,255,.95) 0%, rgba(255,255,255,.35) 45%, rgba(255,255,255,.08) 68%, rgba(255,255,255,0) 80%)',
      filter: 'blur(28px)',
      transition: 'opacity 0.25s ease'
    }} aria-hidden="true" data-id="element-51" />

      {/* Spline 3D viewer (leave watermark alone; we cover it visually) */}
      <spline-viewer className="relative z-10 block w-full h-full" loading-anim="true" url="https://prod.spline.design/kZDDjO5HuC9GJUM2/scene.splinecode" data-id="element-52" />

      {/* ===== Visual covers (safe on iOS/mobile) ===== */}

      {/* Bottom-right pill to cover the Spline badge */}
      <div className="pointer-events-none absolute z-20" style={{
      right: '12px',
      bottom: '12px',
      width: '220px',
      height: '64px',
      borderRadius: '999px',
      background: 'linear-gradient(to top, rgba(10,15,26,1), rgba(10,15,26,0))',
      filter: 'blur(10px)',
      opacity: 0.98
    }} aria-hidden="true" data-id="element-53" />

      {/* Full-width bottom fade to blend robot into the login section (acts like a negative margin overlap) */}
      <div className="pointer-events-none absolute inset-x-0 z-10" style={{
      bottom: 0,
      height: '86px',
      background: 'linear-gradient(to bottom, rgba(0,0,0,0), rgba(10,15,26,1))'
    }} aria-hidden="true" data-id="element-54" />

      {/* Badge glow behind */}
      <div id="cz-glow" ref={glowRef} className="absolute pointer-events-none" style={{
      zIndex: 2,
      left: '50%',
      top: '52%',
      width: 'calc(88px * 1.9)',
      height: 'calc(88px * 1.9)',
      transformOrigin: 'center center',
      transform: 'translate(calc(-50% + 0px), calc(-50% + -4px)) rotate(0deg) scale(0.9)',
      ...getGlowStyle(),
      filter: 'blur(24px)',
      willChange: 'transform, opacity, background',
      transformStyle: 'preserve-3d'
    }} aria-hidden="true" data-id="element-55" />

      {/* Black SVG badge in front (appears 0.9s earlier than glow) */}
      <img ref={badgeRef} className="absolute pointer-events-none" src="https://get.clientczar.com/wp-content/uploads/2025/08/ClientCzar-medallion(2-black).svg" alt="ClientCzar medallion" decoding="async" style={{
      zIndex: 3,
      left: '50%',
      top: '52%',
      width: '88px',
      height: '88px',
      aspectRatio: '1',
      transformOrigin: 'center center',
      transform: 'translate(calc(-50% + 0px), calc(-50% + -4px)) rotate(0deg) scale(0.9)',
      opacity: isReady ? 1 : 0,
      mixBlendMode: 'normal',
      filter: 'drop-shadow(0 0 6px rgba(255,255,255,.22))',
      transition: 'opacity 0.6s ease 2.1s, transform 0.12s linear',
      willChange: 'transform',
      transformStyle: 'preserve-3d'
    }} data-id="element-56" />
    </div>;
}
// Extend JSX to include spline-viewer
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'spline-viewer': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement> & {
        url?: string;
        'loading-anim'?: string;
      }, HTMLElement>;
    }
  }
}